// ============================================================
// ColonieAddAddonModal.js
// Add Transport / Late Pickup / Nerd to an existing enrollment
// Can be triggered at any time after first payment
// ============================================================

import React, { useState } from 'react';
import { X, Loader } from 'lucide-react';
import { P } from './ColoniePrintUtils';

const ColonieAddAddonModal = ({ registration, onClose, onSave, apiCall, showError }) => {
  const [addonType, setAddonType] = useState('');
  const [latePickup, setLp]       = useState('3to330');
  const [nerdType, setNT]         = useState('incamp');
  const [transportFee, setTrf]    = useState('');
  const [saving, setSaving]       = useState(false);

  // Disable already-set add-ons
  const hasTransport = !!registration.HasTransport;
  const hasLatePickup = !!registration.LatePickup;
  const hasNerd = !!registration.HasNerd;

  const previewAmount =
    addonType === 'Transport'  ? parseFloat(transportFee || 0) :
    addonType === 'LatePickup' ? (latePickup === '3to330' ? P.latePickup3to330 : P.latePickup3to4) :
    addonType === 'Nerd'       ? (nerdType === 'outcamp' ? P.nerdOutCamp : P.nerdInCamp) : 0;

  const handleSave = async () => {
    if (!addonType) return showError('Select an add-on type');
    if (addonType === 'Transport' && parseFloat(transportFee || 0) <= 0) return showError('Enter transport fee');
    setSaving(true);
    try {
      const result = await apiCall(`/colonie/enrollments/${registration.EnrollmentID}/add-addon`, {
        method: 'POST',
        body: JSON.stringify({ addonType, latePickup, nerdType, transportFee: parseFloat(transportFee || 0) }),
      });
      onSave(result);
    } catch (err) { showError(err.message); }
    finally { setSaving(false); }
  };

  const addonOptions = [
    { v: 'Transport',  label: 'Transportation',  disabled: hasTransport,  note: hasTransport  ? 'Already added' : 'Fee TBD' },
    { v: 'LatePickup', label: 'Late Pickup',      disabled: hasLatePickup, note: hasLatePickup ? 'Already added' : `$${P.latePickup3to330}–$${P.latePickup3to4}` },
    { v: 'Nerd',       label: 'Nerd Package',     disabled: hasNerd,       note: hasNerd       ? 'Already added' : `$${P.nerdInCamp}/$${P.nerdOutCamp}` },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 bg-gradient-to-r from-amber-500 to-orange-500 rounded-t-2xl">
          <div>
            <h2 className="text-lg font-bold text-white">Add Add-on</h2>
            <p className="text-amber-100 text-sm">{registration.ChildName}</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white p-1.5"><X size={18}/></button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-xs text-gray-500">
            Add-ons increase the total charged. The new balance will appear immediately and can be paid separately.
          </p>

          {/* Add-on type selector */}
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">Add-on Type</p>
            <div className="space-y-2">
              {addonOptions.map(o => (
                <button key={o.v} onClick={() => !o.disabled && setAddonType(o.v)} disabled={o.disabled}
                  className={`w-full flex items-center justify-between p-3 rounded-xl border-2 text-left transition-all ${
                    o.disabled ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                    : addonType === o.v ? 'border-amber-500 bg-amber-50'
                    : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <span className={`font-medium text-sm ${addonType === o.v && !o.disabled ? 'text-amber-800' : 'text-gray-700'}`}>{o.label}</span>
                  <span className="text-xs text-gray-400">{o.note}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Sub-options */}
          {addonType === 'Transport' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Transport Fee ($)</label>
              <input type="number" value={transportFee} onChange={e => setTrf(e.target.value)}
                placeholder="Enter amount"
                className="w-full border border-gray-300 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-amber-500"/>
            </div>
          )}

          {addonType === 'LatePickup' && (
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Pick-up Window</p>
              <div className="flex gap-2">
                {[
                  { v: '3to330', label: '3:00–3:30 PM', price: `$${P.latePickup3to330}` },
                  { v: '3to4',   label: '3:00–4:00 PM', price: `$${P.latePickup3to4}` },
                ].map(o => (
                  <button key={o.v} onClick={() => setLp(o.v)}
                    className={`flex-1 p-3 rounded-xl border-2 text-center text-sm font-medium ${latePickup === o.v ? 'border-amber-500 bg-amber-50 text-amber-800' : 'border-gray-200 text-gray-600'}`}>
                    <div>{o.label}</div>
                    <div className="font-bold mt-0.5">{o.price}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {addonType === 'Nerd' && (
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Nerd Type</p>
              <div className="flex gap-2">
                {[
                  { v: 'incamp',  label: 'In Summer Camp', price: `$${P.nerdInCamp}` },
                  { v: 'outcamp', label: 'Outside Camp',   price: `$${P.nerdOutCamp}` },
                ].map(o => (
                  <button key={o.v} onClick={() => setNT(o.v)}
                    className={`flex-1 p-3 rounded-xl border-2 text-center text-sm font-medium ${nerdType === o.v ? 'border-amber-500 bg-amber-50 text-amber-800' : 'border-gray-200 text-gray-600'}`}>
                    <div>{o.label}</div>
                    <div className="font-bold mt-0.5">{o.price}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Preview */}
          {addonType && previewAmount > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex justify-between items-center">
              <span className="text-sm text-amber-800 font-medium">Amount added to total</span>
              <span className="text-lg font-bold text-amber-700">+${previewAmount.toFixed(2)}</span>
            </div>
          )}
        </div>

        <div className="flex gap-3 p-5 pt-0">
          <button onClick={onClose} className="flex-1 py-3 border border-gray-300 rounded-xl font-semibold text-gray-700">Cancel</button>
          <button onClick={handleSave} disabled={saving || !addonType}
            className="flex-1 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <Loader size={16} className="animate-spin"/> : null}
            {saving ? 'Adding…' : `Add ${addonType || 'Add-on'}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ColonieAddAddonModal;
